// DOM helpers for discovering initial `.luis` sources and pairing output tags.
import type { LuisSourceElement } from './context'
import { assertSecurityDependency, isAllowedLuisSourceUrl, rethrowSecurityError } from '@lentystyle/guard/runtime'
import { applyRuntimeStyleNonce } from '../style-nonce'

const SOURCE_SELECTOR = "link[rel~='stylesheet'][href][data-luis], style[data-luis]"
const trustedLuisSources = new WeakSet<LuisSourceElement>()
const trustedLuisSourceSnapshots = new WeakMap<LuisSourceElement, { href?: string; inlineText?: string }>()
let luisSourceRegistrationFrozen = false

// Find all supported `.luis` sources in current DOM order.
export function findLuisSourceElements(): LuisSourceElement[] {
  const result: LuisSourceElement[] = []
  const candidates = document.querySelectorAll(SOURCE_SELECTOR)
  for (const candidate of candidates) {
    if (candidate instanceof HTMLLinkElement && isLuisStylesheetLink(candidate) && shouldIncludeTrustedSource(candidate)) {
      result.push(candidate)
      continue
    }

    if (candidate instanceof HTMLStyleElement && isLuisInlineStyleTag(candidate) && shouldIncludeTrustedSource(candidate)) {
      result.push(candidate)
    }
  }
  return result
}

// Accept authored `.luis` links only when `data-luis` is present and URL policy allows them.
export function isLuisStylesheetLink(link: HTMLLinkElement): boolean {
  const href = link.getAttribute('href') ?? ''
  return link.hasAttribute('data-luis')
    && /\.luis($|[?#])/i.test(href)
    && isSafeStylesheetHref(href)
}

// Detect inline `.luis` sources declared with `data-luis`.
export function isLuisInlineStyleTag(styleTag: HTMLStyleElement): boolean {
  return styleTag.hasAttribute('data-luis')
}

// Resolve or create the generated style tag paired with one source node.
export function ensureCompiledStyleTag(source: LuisSourceElement): HTMLStyleElement {
  const styleId = ensureStyleTagId(source)
  let styleTag = document.getElementById(styleId) as HTMLStyleElement | null
  if (!styleTag) {
    styleTag = document.createElement('style')
    styleTag.id = styleId
    resolveStyleInsertionTarget(source).insertAdjacentElement('afterend', styleTag)
  }
  applyRuntimeStyleNonce(styleTag)
  return styleTag
}

// Keep observed-only runtime output after preloaded global CSS when available.
function resolveStyleInsertionTarget(source: LuisSourceElement): Element {
  if (!(source instanceof HTMLLinkElement) || source.getAttribute('data-luis-output') !== 'observed-only') {
    return source
  }

  const generatedLinks = document.querySelectorAll('link[data-hybrid-generated="true"]')
  return generatedLinks[generatedLinks.length - 1] ?? source
}

// Assign one stable generated-style id to each source node.
export function ensureStyleTagId(source: LuisSourceElement): string {
  if (source.dataset.luisStyleId) {
    return source.dataset.luisStyleId
  }

  const id = `lentystyle-style-${Math.random().toString(36).slice(2, 10)}`
  source.dataset.luisStyleId = id
  return id
}

// Freeze new runtime source registration after first trusted compile cycle.
export function freezeLuisSourceRegistration(): void {
  luisSourceRegistrationFrozen = true
}

// Resolve locked href for trusted link sources to prevent post-boot tampering.
export function resolveLuisSourceHref(link: HTMLLinkElement): string {
  const snapshot = trustedLuisSourceSnapshots.get(link)
  return luisSourceRegistrationFrozen && snapshot?.href ? snapshot.href : link.href
}

// Resolve locked inline text for trusted style sources to prevent post-boot tampering.
export function resolveLuisInlineSourceText(styleTag: HTMLStyleElement): string {
  const snapshot = trustedLuisSourceSnapshots.get(styleTag)
  return luisSourceRegistrationFrozen && typeof snapshot?.inlineText === 'string'
    ? snapshot.inlineText
    : styleTag.textContent ?? ''
}

// Register source only during initial discovery and capture its immutable snapshot once.
function shouldIncludeTrustedSource(source: LuisSourceElement): boolean {
  if (luisSourceRegistrationFrozen && !trustedLuisSources.has(source)) {
    return false
  }

  trustedLuisSources.add(source)
  registerLuisSourceSnapshot(source)
  return true
}

// Validate stylesheet href against runtime source URL policy.
function isSafeStylesheetHref(rawHref: string): boolean {
  assertSecurityDependency(typeof document !== 'undefined' && Boolean(document.baseURI), 'document.baseURI')
  try {
    return isAllowedLuisSourceUrl(rawHref, document.baseURI)
  } catch (error) {
    rethrowSecurityError(error)
    return false
  }
}

// Capture initial trusted source payload used for immutable runtime compile.
function registerLuisSourceSnapshot(source: LuisSourceElement): void {
  if (trustedLuisSourceSnapshots.has(source)) {
    return
  }

  if (source instanceof HTMLLinkElement) {
    trustedLuisSourceSnapshots.set(source, { href: source.href })
    return
  }

  trustedLuisSourceSnapshots.set(source, { inlineText: source.textContent ?? '' })
}
